/**
 * CurrencyInput — a text input that shows thousands separators while typing
 * (e.g. 1,000,000 instead of 1000000) so large amounts are readable.
 *
 * Controlled: `value` is the raw number (or "" / null when empty). `onChange` is called
 * with the raw NUMBER (or "" when cleared) — NOT the event — so callers store clean values.
 *
 * Use anywhere we take a currency/large-number amount (price, rent, costs, etc.).
 *
 *   <CurrencyInput value={price} onChange={setPrice} style={...} />
 */
export default function CurrencyInput({ value, onChange, allowDecimals = false, style, ...rest }) {
  const display =
    value === "" || value === null || value === undefined || isNaN(value)
      ? ""
      : Number(value).toLocaleString(undefined, allowDecimals ? { maximumFractionDigits: 2 } : {});

  const handleChange = (e) => {
    // Strip everything except digits (and a single dot when decimals are allowed).
    let raw = e.target.value.replace(allowDecimals ? /[^0-9.]/g : /[^0-9]/g, "");
    if (allowDecimals) {
      const firstDot = raw.indexOf(".");
      if (firstDot !== -1) {
        raw = raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, "");
      }
    }
    if (raw === "" || raw === ".") {
      onChange("");
      return;
    }
    // Keep a trailing "." or "x." (mid-typing decimals) from being dropped by Number().
    if (allowDecimals && (raw.endsWith(".") || /\.\d?$/.test(raw))) {
      onChange(raw);
      return;
    }
    const num = Number(raw);
    if (!isNaN(num)) onChange(num);
  };

  return (
    <input
      type="text"
      inputMode={allowDecimals ? "decimal" : "numeric"}
      value={display}
      onChange={handleChange}
      style={style}
      {...rest}
    />
  );
}
